/**
 * Pricing a basket whose legs sit on different expiries — ROADMAP 9.67.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT `marginFor.ts`
 *
 * That adapter feeds the margin model from the CHAIN — one expiry, one
 * forward from its own at-the-money straddle, one volatility per token from
 * the pass the greeks column already ran. It is the right shape for the
 * ladder, and it is the reason the builder said "margin is estimated one
 * expiry at a time" and then showed nothing at all for a calendar.
 *
 * A basket is not one expiry. So this solves each leg on its own terms and
 * states, in each case, how good the input was:
 *
 *   ON THE LADDER'S EXPIRY   the chain's solved IV and its parity forward.
 *                            Identical to what the Terminal shows, because
 *                            it IS what the Terminal shows.
 *
 *   ANY OTHER EXPIRY         the forward is `spot × e^(rT)` — carry, with no
 *                            parity to check it against, because recovering
 *                            one needs that chain's at-the-money straddle
 *                            subscribed and the builder holds only the legs
 *                            the trader clicked. The volatility is then
 *                            solved from the leg's own price by the same
 *                            bisection the chain uses.
 *
 * The approximation is in the forward, and it is small on an index: the basis
 * is carry plus a dividend drift of a few points over weeks. It is named here
 * rather than buried because it is the one input nobody can see.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MARGIN IS SUMMED PER EXPIRY, AND THAT IS CONSERVATIVE
 *
 * `estimateMargin` scans ONE expiry: sixteen scenarios on one forward over
 * one remaining term. Two expiries are two scans, and this adds them.
 *
 * The exchange does not. SPAN gives a calendar spread a credit for the offset
 * between the two legs, so the real blocked amount is LOWER than this.
 * Over-stating a margin requirement is the safe direction to be wrong in — a
 * basket shown as needing more than it does is one a trader sizes down,
 * never one that gets rejected at the broker for insufficient funds — and it
 * is a far better answer than the dash this used to print.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  estimateMargin,
  impliedVol,
  yearsToExpiry,
  type CalendarLeg,
  type MarginEstimate,
  type ResolvedLeg,
} from '@apna/shared'
import { RISK_FREE_RATE, type ChainGreeks } from '@/screens/terminal/useChainGreeks'
import type { BasketLegDto } from '@/types/basket'

/** A leg with everything the models need, and where each number came from. */
export interface PricedBasketLeg extends CalendarLeg {
  readonly token: number
  readonly expiry: string
  /** True when the IV came from the chain's own pass rather than a solve here. */
  readonly fromChain: boolean
}

export interface SolveInput {
  legs: readonly BasketLegDto[]
  /** The same legs priced at the crossing touch — see `resolvedLegsOf`. */
  resolved: readonly ResolvedLeg[]
  lotSize: number
  spot: number
  /** The expiry the ladder is showing, whose greeks pass we can reuse. */
  chainExpiry: string | null
  greeks: ChainGreeks
  now?: Date
}

export type SolvedBasket =
  | { readonly ok: true; readonly legs: readonly PricedBasketLeg[] }
  | { readonly ok: false; readonly why: string }

/**
 * Every leg, with a volatility, a forward and a term.
 *
 * FAILS AS A WHOLE. A margin or a payoff computed from eight of nine legs is
 * not a cautious estimate — it is a specific claim about a position that is
 * not the one being placed, and the missing leg is usually the far wing that
 * caps the loss.
 */
export function solveBasket({
  legs,
  resolved,
  lotSize,
  spot,
  chainExpiry,
  greeks,
  now = new Date(),
}: SolveInput): SolvedBasket {
  if (legs.length === 0) return { ok: false, why: 'Nothing to price.' }
  if (!(lotSize > 0)) return { ok: false, why: 'No lot size for this contract.' }
  if (!(spot > 0)) return { ok: false, why: 'Waiting for the underlying price…' }

  const out: PricedBasketLeg[] = []

  for (const [i, leg] of legs.entries()) {
    const priced = resolved[i]
    if (!priced) return { ok: false, why: 'Waiting for prices…' }

    const avgPrice = (priced.side === 'SELL' ? priced.bid : priced.ask) ?? priced.ltp
    if (!(avgPrice > 0)) {
      return { ok: false, why: `No price on ${leg.strike} ${leg.optionType} yet.` }
    }

    const years = yearsToExpiry(new Date(`${leg.expiry}T15:30:00+05:30`), now)
    if (!(years > 0)) {
      return { ok: false, why: `${leg.strike} ${leg.optionType} has already expired.` }
    }

    const onChain = leg.expiry === chainExpiry
    const forward =
      onChain && greeks.forward !== null && greeks.forward > 0
        ? greeks.forward
        : spot * Math.exp(RISK_FREE_RATE * years)

    const chainIv = onChain ? greeks.byToken.get(leg.token)?.iv : undefined
    let iv = chainIv
    if (iv === undefined) {
      const solved = impliedVol({
        price: avgPrice,
        forward,
        strike: leg.strike,
        years,
        rate: RISK_FREE_RATE,
        optionType: leg.optionType,
      })
      if (!solved.solved) {
        return {
          ok: false,
          why: `${leg.strike} ${leg.optionType}: ${solved.why.toLowerCase()}`,
        }
      }
      iv = solved.iv
    }

    out.push({
      token: leg.token,
      expiry: leg.expiry,
      netQty: (leg.side === 'BUY' ? 1 : -1) * leg.lots * lotSize,
      avgPrice,
      strike: leg.strike,
      optionType: leg.optionType,
      years,
      iv,
      forward,
      fromChain: chainIv !== undefined,
    })
  }

  return { ok: true, legs: out }
}

/**
 * Initial margin for the whole basket: one scan per expiry, added up.
 *
 * See the header for why adding them is conservative rather than wrong.
 * `expiries` on the result is what the caller tells the trader, so a figure
 * covering two scans is never read as one.
 */
export function basketMargin(
  legs: readonly PricedBasketLeg[],
  opts: { spot: number; isExpiryDay: (expiry: string) => boolean },
): MarginEstimate & { readonly expiries?: number } {
  if (legs.length === 0) return { ok: false, why: 'no legs' }

  const groups = new Map<string, PricedBasketLeg[]>()
  for (const leg of legs) {
    const at = groups.get(leg.expiry)
    if (at) at.push(leg)
    else groups.set(leg.expiry, [leg])
  }

  let scanRisk = 0
  let exposure = 0
  let expiryElm = 0
  let premium = 0
  let total = 0
  let priceScanRange = 0
  let volScanRange = 0

  for (const [expiry, group] of groups) {
    const one = estimateMargin({
      legs: group.map((l) => ({
        netQty: l.netQty,
        strike: l.strike,
        optionType: l.optionType,
        iv: l.iv,
        price: l.avgPrice,
      })),
      // Every leg of a group shares an expiry, so they share these two.
      forward: group[0]!.forward,
      spot: opts.spot,
      years: group[0]!.years,
      rate: RISK_FREE_RATE,
      isExpiryDay: opts.isExpiryDay(expiry),
    })
    if (!one.ok) return one

    scanRisk += one.margin.scanRisk
    exposure += one.margin.exposure
    expiryElm += one.margin.expiryElm
    premium += one.margin.premium
    total += one.margin.total
    priceScanRange = Math.max(priceScanRange, one.margin.priceScanRange)
    volScanRange = Math.max(volScanRange, one.margin.volScanRange)
  }

  return {
    ok: true,
    expiries: groups.size,
    margin: { scanRisk, exposure, expiryElm, premium, total, priceScanRange, volScanRange },
  }
}
